// SPDX-License-Identifier: AGPL-3.0-or-later

import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { Button, Card, Input, Label, Stack } from '@intakeform/ui';
import { useMe } from '../../api/auth.js';
import {
  useIssueInvite,
  useRemoveMember,
  useRevokeInvite,
  useTeam,
} from '../../api/customer-enablement.js';

export function formatRole(role: string): string {
  switch (role) {
    case 'admin':
      return 'Administrator';
    case 'clinician':
      return 'Clinician';
    case 'hospital':
      return 'Hospital';
    case 'doctor_office':
      return 'Doctor Office';
    case 'xray':
      return 'X-ray';
    case 'auxiliary':
      return 'Auxiliary Services';
    default:
      return role;
  }
}

export function TeamRoute() {
  const { data: me } = useMe();
  const team = useTeam();
  const invite = useIssueInvite();
  const revoke = useRevokeInvite();
  const remove = useRemoveMember();

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<
    'clinician' | 'admin' | 'hospital' | 'doctor_office' | 'xray' | 'auxiliary'
  >('clinician');
  const [displayName, setDisplayName] = useState('');

  if (!me) return null;
  if (me.user.role !== 'admin') {
    return (
      <Card heading="Team settings" tone="danger">
        <p>Only administrators can view or invite team members.</p>
        <p style={{ marginTop: 12 }}>
          <Link to="/dashboard">Back to dashboard</Link>
        </p>
      </Card>
    );
  }

  const onInvite = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    invite.mutate(
      {
        email: email.trim(),
        role,
        ...(displayName.trim().length > 0 ? { displayName: displayName.trim() } : {}),
      },
      {
        onSuccess: () => {
          setEmail('');
          setDisplayName('');
          setRole('clinician');
        },
      },
    );
  };

  return (
    <div className="space-y-8">
      {/* Invite Clinician Card */}
      <Card heading="Invite a clinician" variant="default">
        <form onSubmit={onInvite} style={{ maxWidth: 560, marginTop: 12 }}>
          <Stack gap={16}>
            <Stack gap={4}>
              <Label htmlFor="invite-email" required>
                Email
              </Label>
              <Input
                id="invite-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                required
              />
            </Stack>

            <Stack gap={4}>
              <Label htmlFor="invite-name">Display name (optional)</Label>
              <Input
                id="invite-name"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.currentTarget.value)}
              />
            </Stack>

            <Stack gap={4}>
              <Label htmlFor="invite-role">Role</Label>
              <select
                id="invite-role"
                value={role}
                onChange={(e) =>
                  setRole(
                    e.currentTarget.value as
                      'clinician' | 'admin' | 'hospital' | 'doctor_office' | 'xray' | 'auxiliary',
                  )
                }
                className="w-full px-3 py-2 text-sm bg-surface border border-border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="clinician">Clinician</option>
                <option value="admin">Administrator</option>
                <option value="hospital">Hospital</option>
                <option value="doctor_office">Doctor Office</option>
                <option value="xray">X-ray</option>
                <option value="auxiliary">Auxiliary Services</option>
              </select>
            </Stack>

            {invite.error ? (
              <div className="error-banner text-sm" role="alert" aria-live="polite">
                {invite.error.code === 'invite_in_use'
                  ? 'An invite for that email is already pending.'
                  : invite.error.code === 'email_in_use'
                    ? 'A user with that email already exists in this clinic.'
                    : invite.error.code === 'invite_delivery_failed'
                      ? 'The invite email could not be sent, so the invite was cancelled. Check the email settings and try again.'
                      : invite.error.message}
              </div>
            ) : null}

            {invite.isSuccess ? (
              <div
                role="status"
                aria-live="polite"
                className="text-sm text-muted-foreground bg-surface-sunken p-3 rounded-md border border-border"
              >
                Invite emailed to {invite.data.email}.
                {invite.data.devLink !== undefined ? (
                  <>
                    {' '}
                    Dev link:&nbsp;
                    <code
                      style={{ wordBreak: 'break-all' }}
                      className="select-all text-xs font-mono"
                    >
                      {invite.data.devLink}
                    </code>
                  </>
                ) : null}
              </div>
            ) : null}

            <Button type="submit" variant="primary" disabled={invite.isPending}>
              {invite.isPending ? 'Sending…' : 'Send invite'}
            </Button>
          </Stack>
        </form>
      </Card>

      {/* Pending Invites Card */}
      <Card heading="Pending invites" variant="default">
        {team.isLoading ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : team.data && team.data.pendingInvites.length > 0 ? (
          <ul style={{ listStyle: 'none', padding: 0, display: 'grid', gap: 8 }} className="mt-2">
            {team.data.pendingInvites.map((inv) => (
              <li
                key={inv.id}
                className="list-row flex justify-between items-center p-3 bg-surface-sunken border border-border rounded-md"
              >
                <span className="text-sm">
                  {inv.email}{' '}
                  <span className="text-xs text-muted-foreground bg-surface px-2 py-1 rounded ml-2">
                    {formatRole(inv.role)}
                  </span>
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => revoke.mutate(inv.id)}
                  disabled={revoke.isPending}
                  className="text-xs text-danger hover:text-danger-foreground hover:bg-danger-subtle px-3 py-1.5"
                >
                  Revoke
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted-foreground text-sm mt-2">No pending invites.</p>
        )}
      </Card>

      {/* Members Card */}
      <Card heading="Members" variant="default">
        {team.isLoading ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : team.data ? (
          <ul style={{ listStyle: 'none', padding: 0, display: 'grid', gap: 8 }} className="mt-2">
            {team.data.members.map((m) => (
              <li
                key={m.id}
                className="list-row flex justify-between items-center p-3 bg-surface-sunken border border-border rounded-md"
              >
                <span className="text-sm font-medium">
                  {m.displayName}{' '}
                  <span className="text-xs text-muted-foreground font-normal block md:inline md:ml-2">
                    · {m.email} ·{' '}
                    <span className="bg-surface px-2 py-0.5 rounded ml-1 font-semibold">
                      {formatRole(m.role)}
                    </span>
                  </span>
                </span>
                {m.id !== me.user.id ? (
                  <Button
                    type="button"
                    variant="danger"
                    onClick={() => remove.mutate(m.id)}
                    disabled={remove.isPending}
                    className="text-xs px-3 py-1.5"
                  >
                    Remove
                  </Button>
                ) : (
                  <span className="text-xs text-muted-foreground bg-primary-subtle text-primary font-semibold px-2 py-1 rounded">
                    You
                  </span>
                )}
              </li>
            ))}
          </ul>
        ) : null}
        {remove.error && remove.error.code === 'last_admin' ? (
          <div className="error-banner mt-3 text-sm" role="alert" aria-live="polite">
            You can't remove the last administrator. Promote another member first.
          </div>
        ) : null}
      </Card>
    </div>
  );
}
